import React from 'react';
import * as Sentry from '@sentry/react';
import logger from '../utils/logger';
import { isChunkLoadError } from '../utils/lazyWithRetry';
import styles from './ErrorBoundary.module.scss';

// Session-scoped guard so a hard-broken build can't loop the user forever.
// lazyWithRetry uses its own key; this one only fires when a chunk-load error
// slipped past Suspense and reached the boundary.
const BOUNDARY_RELOAD_KEY = 'gs:chunkBoundaryReloaded';

function tryBoundaryReload() {
  try {
    if (window.sessionStorage.getItem(BOUNDARY_RELOAD_KEY) === '1') {
      return false;
    }
    window.sessionStorage.setItem(BOUNDARY_RELOAD_KEY, '1');
  } catch (_) {
    /* storage blocked; reload anyway, worst case is one extra retry */
  }
  window.location.reload();
  return true;
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Stale-bundle-after-deploy: the user's open tab is on an old bundle and
    // React.lazy can't fetch a removed hashed chunk. Don't spam Sentry with
    // these (expected for any tab open during a deploy) and silently reload
    // to pick up the new bundle instead of showing an error screen.
    if (isChunkLoadError(error)) {
      logger.warn('Chunk load error reached ErrorBoundary', error);
      if (tryBoundaryReload()) return;
    }

    this.setState({ error, errorInfo });
    logger.error('Error caught by boundary:', error);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack } },
    });
  }

  handleTryAgain = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.fallback}>
          <div className={styles.card}>
            <h1 className={styles.title}>Something went wrong</h1>
            <p className={styles.body}>
              Please try again. If the problem continues, refresh the page.
            </p>
            <button
              type="button"
              className={styles.button}
              onClick={this.handleTryAgain}
            >
              Try again
            </button>
            {process.env.NODE_ENV === 'development' && (
              <details className={styles.details}>
                <summary>Developer details</summary>
                <div className={styles.stack}>
                  <strong>Error:</strong>{' '}
                  {this.state.error && this.state.error.toString()}
                  <br />
                  <strong>Component stack:</strong>
                  <pre>
                    {this.state.errorInfo &&
                      this.state.errorInfo.componentStack}
                  </pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
